/**
 * 결제 통계 카드 — 큰 숫자에 강건한 레이아웃.
 *
 *  설계:
 *   - 세로 스택 (title 위, 큰 숫자 가운데, 보조 정보 아래)
 *   - 큰 금액은 한국식 압축 단위 (1.2억, 3,400만) — 카드 폭 초과 방지
 *   - 정확한 값은 title 속성(툴팁) 으로 노출
 *   - 모바일 1열 / 태블릿 2열 / 데스크탑 4열
 *
 *  variant:
 *   - default      : 카드형 (대시보드 본문)
 *   - headerRight  : 컴팩트 (헤더 우측 슬롯)
 */

import type { PaymentStatistics } from '../../types/payment';

interface PaymentStatsProps {
  statistics: PaymentStatistics;
  variant?: 'default' | 'headerRight';
  /** 카드 타이틀 오버라이드 — 오프라인(수동 발급) 페이지는 "결제" 대신 "발급" 문구 사용 */
  labels?: {
    total?: string;
    count?: string;
    cancel?: string;
    net?: string;
    netSub?: string;
  };
}

/** 한국식 압축 단위 — 1억 이상은 "1.2억", 1만 이상은 "3,400만". */
function formatKrwCompact(n: number): string {
  if (!Number.isFinite(n)) return '0원';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_0000_0000) {
    const value = abs / 1_0000_0000;
    return `${sign}${value.toFixed(value >= 100 ? 0 : 2).replace(/\.?0+$/, '')}억 원`;
  }
  if (abs >= 1_0000) {
    const man = Math.floor(abs / 1_0000);
    const remain = abs % 1_0000;
    if (remain === 0) return `${sign}${man.toLocaleString()}만 원`;
    return `${sign}${man.toLocaleString()}만 ${remain.toLocaleString()}원`;
  }
  return `${sign}${abs.toLocaleString()}원`;
}

/** 카드 1장 */
interface StatCard {
  title: string;
  /** 주 값 — 표시용 (압축). 정확값은 valueExact 로. */
  valueDisplay: string;
  valueExact: string;
  /** 보조 값 (예: 건수 또는 부가 설명) */
  sub?: string;
  icon: string;
  iconBg: string;
  iconColor: string;
}

export function PaymentStats({
  statistics,
  variant = 'default',
  labels,
}: PaymentStatsProps) {
  const isHeaderRight = variant === 'headerRight';

  const total = statistics.totalPaymentAmount ?? 0;
  const totalCount = statistics.totalPaymentCount ?? 0;
  const cancelAmt = statistics.cancelAmount ?? 0;
  const cancelCnt = statistics.cancelCount ?? 0;
  const netAmt = statistics.netAmount ?? 0;

  const cards: StatCard[] = [
    {
      title: labels?.total ?? '총 결제 금액',
      valueDisplay: formatKrwCompact(total),
      valueExact: `${total.toLocaleString()}원`,
      sub: `${totalCount.toLocaleString()}건`,
      icon: '💰',
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
    {
      title: labels?.count ?? '결제 건수',
      valueDisplay: `${totalCount.toLocaleString()}건`,
      valueExact: `${totalCount.toLocaleString()}건`,
      icon: '📊',
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      title: labels?.cancel ?? '취소/회수',
      valueDisplay: formatKrwCompact(cancelAmt),
      valueExact: `${cancelAmt.toLocaleString()}원`,
      sub: `${cancelCnt.toLocaleString()}건`,
      icon: '↶',
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600',
    },
    {
      title: labels?.net ?? '순 결제 금액',
      valueDisplay: formatKrwCompact(netAmt),
      valueExact: `${netAmt.toLocaleString()}원`,
      sub: labels?.netSub ?? '결제 − 취소·회수',
      icon: '💳',
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
    },
  ];

  if (isHeaderRight) {
    // 헤더 우측 — 한 줄 가로 4분할 (모바일 2x2)
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {cards.map((c) => (
          <div
            key={c.title}
            className="bg-white rounded-md border border-gray-200 px-2.5 py-1.5 min-w-0"
            title={`${c.title} · ${c.valueExact}${c.sub ? ` · ${c.sub}` : ''}`}
          >
            <p className="text-[10px] text-gray-500 truncate">{c.title}</p>
            <p className="text-sm font-bold text-gray-900 truncate tabular-nums">
              {c.valueDisplay}
            </p>
            {c.sub && (
              <p className="text-[10px] text-gray-400 truncate">{c.sub}</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  // default — 카드형 본문
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map((c) => (
        <div
          key={c.title}
          className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow min-w-0"
        >
          {/* 상단: 타이틀 + 아이콘 */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <p className="text-sm text-gray-600 truncate">{c.title}</p>
            <div className={`${c.iconBg} ${c.iconColor} rounded-full w-8 h-8 flex items-center justify-center text-base shrink-0`}>
              {c.icon}
            </div>
          </div>

          {/* 큰 값 — 압축 단위, 카드 폭 초과 방지 */}
          <p
            className="text-lg font-bold text-gray-900 tabular-nums truncate"
            title={c.valueExact}
          >
            {c.valueDisplay}
          </p>

          {/* 보조 — 건수 또는 설명 */}
          {c.sub && (
            <p className="mt-1 text-xs text-gray-500 truncate">{c.sub}</p>
          )}
        </div>
      ))}
    </div>
  );
}
